/**
 * Shared, framework-agnostic helpers for AHRI multi-match component selection.
 *
 * An AHRI system can have multiple approved products per slot (outdoor /
 * indoor / furnace). Solr indexes them as multi-valued `prodmap_{field}` plus a
 * single-valued `prodmap_{field}_primary` hint. These helpers parse those into
 * per-slot option lists and resolve an inventory-first default selection
 * (hiding out-of-stock alternatives at the selected warehouse).
 *
 * Pure functions only — no React, no network — so they can be unit tested and
 * reused by both the embeddable widget and the in-app shop page.
 */

export const AHRI_COMPONENT_SLOTS = [
  { type: "outdoor", field: "outdoormodelnumber", label: "Outdoor Unit" },
  { type: "indoor", field: "indoormodelnumber", label: "Indoor Unit" },
  { type: "furnace", field: "furnacemodelnumber", label: "Furnace" },
];

function toCodeList(raw) {
  const source = Array.isArray(raw) ? raw : raw !== undefined && raw !== null ? [raw] : [];
  const seen = new Set();
  const out = [];
  for (const entry of source) {
    const code = String(entry || "").trim();
    if (code && !seen.has(code)) {
      seen.add(code);
      out.push(code);
    }
  }
  return out;
}

/**
 * Read all approved product codes for one slot from a Solr doc, with the
 * primary (slot 1) code first when the `_primary` hint is present.
 */
export function getSlotCodesFromDoc(doc, fieldSuffix) {
  if (!doc || typeof doc !== "object") return [];
  const codes = toCodeList(doc[`prodmap_${fieldSuffix}`]);
  const primaryRaw = doc[`prodmap_${fieldSuffix}_primary`];
  const primary = String(Array.isArray(primaryRaw) ? primaryRaw[0] : primaryRaw || "").trim();
  if (primary && codes.includes(primary)) {
    return [primary, ...codes.filter((code) => code !== primary)];
  }
  return codes;
}

/**
 * Parse per-slot component options from a Solr doc.
 * Returns `{ outdoor: { field, label, model, codes }, indoor: {...}, furnace: {...} }`.
 */
export function parseComponentOptions(doc, models = {}) {
  const result = {};
  for (const slot of AHRI_COMPONENT_SLOTS) {
    result[slot.type] = {
      type: slot.type,
      field: slot.field,
      label: slot.label,
      model: models[slot.type] || "",
      codes: getSlotCodesFromDoc(doc, slot.field),
    };
  }
  return result;
}

/** True when any slot exposes more than one approved option. */
export function hasComponentChoices(componentOptions) {
  if (!componentOptions || typeof componentOptions !== "object") return false;
  return Object.values(componentOptions).some(
    (slot) => Array.isArray(slot?.codes) && slot.codes.length > 1
  );
}

/**
 * Resolve the visible options and default selection for a single slot.
 *
 * `isInStock(code)` returns whether a code has stock at the selected warehouse.
 * When `hideOutOfStock` is true, out-of-stock codes are dropped from
 * `visibleCodes`; if that leaves nothing, every code stays visible as a
 * fallback. The default selection prefers a previous (still-visible) choice,
 * then the first in-stock code, then the primary.
 */
export function resolveSlotSelection(
  codes,
  { isInStock = null, hideOutOfStock = true, previousSelection = "" } = {}
) {
  const all = toCodeList(codes);
  if (!all.length) {
    return { codes: [], visibleCodes: [], inStockCodes: [], selectedCode: "" };
  }
  const inStockCodes = typeof isInStock === "function" ? all.filter((code) => isInStock(code)) : all.slice();
  const visibleCodes = hideOutOfStock ? (inStockCodes.length ? inStockCodes : all) : all;
  const previous = String(previousSelection || "").trim();
  let selectedCode = "";
  if (previous && visibleCodes.includes(previous)) {
    selectedCode = previous;
  } else {
    selectedCode = inStockCodes[0] || visibleCodes[0] || all[0] || "";
  }
  return { codes: all, visibleCodes, inStockCodes, selectedCode };
}

/**
 * Resolve a full system configuration (all slots) plus a buildable flag.
 *
 * `buildable` is true when every slot that has options has at least one
 * in-stock option at the selected warehouse.
 */
export function resolveConfiguration(
  componentOptions,
  { isInStock = null, hideOutOfStock = true, previousSelections = {} } = {}
) {
  const selections = {};
  const visible = {};
  let buildable = true;
  let anySlot = false;
  for (const slot of AHRI_COMPONENT_SLOTS) {
    const option = componentOptions?.[slot.type];
    const codes = option?.codes || [];
    if (!codes.length) {
      selections[slot.type] = "";
      visible[slot.type] = [];
      continue;
    }
    anySlot = true;
    const resolved = resolveSlotSelection(codes, {
      isInStock,
      hideOutOfStock,
      previousSelection: previousSelections?.[slot.type] || "",
    });
    selections[slot.type] = resolved.selectedCode;
    visible[slot.type] = resolved.visibleCodes;
    if (!resolved.inStockCodes.length) {
      buildable = false;
    }
  }
  return { selections, visible, buildable: anySlot ? buildable : false };
}

// Attribute keys that identify a product rather than describe a meaningful,
// shopper-facing difference. Excluded when computing what distinguishes the
// options in a slot (they're noise: every option trivially differs on them).
const OPTION_DIFF_IGNORE_KEYS = new Set([
  "id",
  "sku",
  "productcode",
  "product_code",
  "name",
  "title",
  "description",
  "longdescription",
  "long_description",
  "shortdescription",
  "short_description",
  "image",
  "imageurl",
  "image_url",
  "url",
  "slug",
  "upc",
  "gtin",
  "ean",
  "mpn",
  "referenceid",
  "reference_id",
]);

function normalizeDiffValue(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    return value
      .map((entry) => (entry === null || entry === undefined ? "" : String(entry).trim()))
      .filter(Boolean)
      .join(", ");
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value).trim();
}

/** Turn an attribute key like `coilWidth` or `coil_width` into `Coil Width`. */
export function prettifyAttributeKey(key) {
  const raw = String(key || "").trim();
  if (!raw) return "";
  const spaced = raw
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();
  return spaced.replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Given the products that fill a single slot, work out which attributes
 * actually distinguish them so the UI can surface the differences (product
 * names are often identical across options).
 *
 * `options`: array of `{ code, attributes?, description?, category? }`.
 * Returns:
 *   - `diffKeys`: original attribute keys whose values differ across options
 *   - `labelsByCode`: `{ [code]: [{ key, keyLabel, value }] }` for the chosen keys
 *   - `descriptionByCode`: `{ [code]: string }`
 *   - `useDescriptionFallback`: true when no attribute differences were found
 *     but the descriptions differ (so the UI can show those instead)
 */
export function computeOptionDifferentiators(options, { maxKeys = 3 } = {}) {
  const list = (Array.isArray(options) ? options : []).filter((entry) => entry && entry.code);
  const result = {
    diffKeys: [],
    labelsByCode: {},
    descriptionByCode: {},
    useDescriptionFallback: false,
  };
  list.forEach((entry) => {
    result.labelsByCode[entry.code] = [];
    result.descriptionByCode[entry.code] = normalizeDiffValue(entry.description);
  });
  if (list.length < 2) return result;

  // Merge category into the comparable attribute set (it can be a useful
  // differentiator) without mutating the caller's objects.
  const attrsByCode = new Map();
  const keyMap = new Map(); // lowercased key -> first-seen original key
  for (const entry of list) {
    const attrs = entry.attributes && typeof entry.attributes === "object" ? { ...entry.attributes } : {};
    if (entry.category && !("category" in attrs) && !("Category" in attrs)) {
      attrs.Category = entry.category;
    }
    attrsByCode.set(entry.code, attrs);
    for (const key of Object.keys(attrs)) {
      const lower = String(key).trim().toLowerCase();
      if (!lower || OPTION_DIFF_IGNORE_KEYS.has(lower)) continue;
      if (!keyMap.has(lower)) keyMap.set(lower, key);
    }
  }

  const candidates = [];
  for (const [lower, originalKey] of keyMap.entries()) {
    const values = list.map((entry) => normalizeDiffValue(attrsByCode.get(entry.code)[originalKey]));
    const presentCount = values.filter((value) => value !== "").length;
    if (!presentCount) continue;
    if (new Set(values).size > 1) {
      candidates.push({ lower, originalKey, presentCount });
    }
  }
  // Prefer attributes present on every option, then alphabetical for stability.
  candidates.sort(
    (a, b) => b.presentCount - a.presentCount || a.lower.localeCompare(b.lower)
  );
  const chosen = candidates.slice(0, Math.max(1, maxKeys));
  result.diffKeys = chosen.map((candidate) => candidate.originalKey);

  for (const entry of list) {
    const attrs = attrsByCode.get(entry.code);
    result.labelsByCode[entry.code] = chosen.map((candidate) => ({
      key: candidate.originalKey,
      keyLabel: prettifyAttributeKey(candidate.originalKey),
      value: normalizeDiffValue(attrs[candidate.originalKey]) || "—",
    }));
  }

  if (!chosen.length) {
    const descriptions = list.map((entry) => result.descriptionByCode[entry.code]);
    const nonEmpty = descriptions.filter(Boolean);
    result.useDescriptionFallback = nonEmpty.length >= 1 && new Set(descriptions).size > 1;
  }

  return result;
}
